import { useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  LayoutDashboard,
  Newspaper,
  Podcast,
  FolderKanban,
  Image,
  Users,
  LogOut,
  Menu,
  X,
  ClipboardList,
  Mail,
  FileText,
} from 'lucide-react';
import NewsManager from '../components/admin/NewsManager';
import EpisodesManager from '../components/admin/EpisodesManager';
import ProjectsManager from '../components/admin/ProjectsManager';
import MediaManager from '../components/admin/MediaManager';
import DashboardOverview from '../components/admin/DashboardOverview';
import ApplicationsManager from '../components/admin/ApplicationsManager';
import CampaignsManager from '../components/admin/CampaignsManager';
import TemplatesManager from '../components/admin/TemplatesManager';

type Tab = 'overview' | 'news' | 'episodes' | 'projects' | 'media' | 'applications' | 'campaigns' | 'templates';

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, profile, logout } = useAdminAuth();

  const tabs = [
    { id: 'overview' as Tab, label: 'Overview', icon: LayoutDashboard },
    { id: 'applications' as Tab, label: 'Applications', icon: ClipboardList },
    { id: 'campaigns' as Tab, label: 'Email Campaigns', icon: Mail },
    { id: 'templates' as Tab, label: 'Email Templates', icon: FileText },
    { id: 'news' as Tab, label: 'News & Blog', icon: Newspaper },
    { id: 'episodes' as Tab, label: 'Episodes', icon: Podcast },
    { id: 'projects' as Tab, label: 'Projects', icon: FolderKanban },
    { id: 'media' as Tab, label: 'Media Library', icon: Image },
  ];

  const handleLogout = async () => {
    if (confirm('Are you sure you want to logout?')) {
      await logout();
    }
  };

  return (
    <div className="min-h-screen bg-black flex">
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-gradient-to-b from-red-900/40 to-black border-r border-yellow-400/30 transition-all duration-300 flex flex-col`}
      >
        <div className="p-4 border-b border-yellow-400/30">
          <div className="flex items-center justify-between mb-4">
            <h1
              className={`font-horizon text-yellow-400 ${
                sidebarOpen ? 'text-xl' : 'text-xs'
              } transition-all`}
            >
              {sidebarOpen ? 'WFP Admin' : 'WFP'}
            </h1>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-yellow-400 transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {sidebarOpen && user && (
            <div className="bg-black/50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={user.full_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
                    <Users className="text-black" size={20} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">
                    {user.full_name}
                  </p>
                  <p className="text-gray-400 text-xs capitalize">{user.role}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeTab === tab.id
                        ? 'bg-yellow-400 text-black'
                        : 'text-gray-300 hover:bg-red-900/30 hover:text-yellow-400'
                    }`}
                    title={tab.label}
                  >
                    <Icon size={20} />
                    {sidebarOpen && <span className="font-bold">{tab.label}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-yellow-400/30">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/30 transition-all"
            title="Logout"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="font-bold">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {activeTab === 'overview' && <DashboardOverview />}
          {activeTab === 'applications' && <ApplicationsManager />}
          {activeTab === 'campaigns' && <CampaignsManager />}
          {activeTab === 'templates' && <TemplatesManager />}
          {activeTab === 'news' && <NewsManager />}
          {activeTab === 'episodes' && <EpisodesManager />}
          {activeTab === 'projects' && <ProjectsManager />}
          {activeTab === 'media' && <MediaManager />}
        </div>
      </main>
    </div>
  );
}
